import { z } from 'zod';
import { cursorQuerySchema } from './core.js';
/* ------------------------------------------------------------------ */
/* Hằng số                                                             */
/* ------------------------------------------------------------------ */
export const COMPLIANCE_ISSUE_TYPES = [
    'content_id_claim',
    'internal_music_claim',
    'license_expired',
    'ad_limited',
    'copyright_strike',
    'community_strike',
];
export const complianceIssueTypeSchema = z.enum(COMPLIANCE_ISSUE_TYPES);
export const COMPLIANCE_ISSUE_TYPE_LABEL = {
    content_id_claim: 'Khiếu nại Content ID',
    internal_music_claim: 'Claim nhầm từ nhạc nội bộ',
    license_expired: 'Giấy phép hết hạn',
    ad_limited: 'Hạn chế quảng cáo',
    copyright_strike: 'Gậy bản quyền',
    community_strike: 'Cảnh cáo cộng đồng',
};
export const COMPLIANCE_ISSUE_TONE = {
    content_id_claim: 'warn',
    internal_music_claim: 'info',
    license_expired: 'warn',
    ad_limited: 'warn',
    copyright_strike: 'bad',
    community_strike: 'bad',
};
export const COMPLIANCE_STATUSES = [
    'needs_response',
    'disputed',
    'network_support',
    'under_review',
    'resolved',
];
export const complianceIssueStatusSchema = z.enum(COMPLIANCE_STATUSES);
export const COMPLIANCE_STATUS_LABEL = {
    needs_response: 'Cần phản hồi',
    disputed: 'Đã gửi tranh chấp',
    network_support: 'Nhờ Network hỗ trợ',
    under_review: 'Đang xem xét',
    resolved: 'Đã xử lý',
};
export const COMPLIANCE_SOURCES = ['manual', 'import', 'auto'];
export const complianceSourceSchema = z.enum(COMPLIANCE_SOURCES);
/** Hành động gợi ý trên nút của bảng sự cố. */
export const SUGGESTED_ACTIONS = [
    'add_whitelist',
    'dispute',
    'ask_network',
    'replace_music',
    'renew_license',
    'review_policy',
];
export const SUGGESTED_ACTION_LABEL = {
    add_whitelist: 'Thêm whitelist',
    dispute: 'Gửi tranh chấp',
    ask_network: 'Nhờ Network hỗ trợ',
    replace_music: 'Thay nhạc khác',
    renew_license: 'Gia hạn giấy phép',
    review_policy: 'Rà soát chính sách',
};
/* ------------------------------------------------------------------ */
/* Sự cố                                                               */
/* ------------------------------------------------------------------ */
export const complianceIssueSchema = z.object({
    id: z.string(),
    channelId: z.string(),
    channelName: z.string(),
    videoRef: z.string().nullable(),
    videoTitle: z.string().nullable(),
    type: complianceIssueTypeSchema,
    claimant: z.string().nullable(),
    dueDate: z.iso.datetime().nullable(),
    status: complianceIssueStatusSchema,
    suggestedAction: z.string().nullable(),
    assigneeId: z.string().nullable(),
    assigneeName: z.string().nullable(),
    source: complianceSourceSchema,
    ticketRef: z.string().nullable(),
    resolvedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
export const complianceLogSchema = z.object({
    id: z.string(),
    issueId: z.string().nullable(),
    text: z.string(),
    userId: z.string().nullable(),
    userName: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const checklistTemplateItemSchema = z.object({
    id: z.string(),
    key: z.string(),
    label: z.string(),
    order: z.number().int(),
    active: z.boolean(),
});
/* ------------------------------------------------------------------ */
/* Input                                                               */
/* ------------------------------------------------------------------ */
export const listIssuesQuerySchema = cursorQuerySchema.extend({
    channelId: z.string().optional(),
    type: complianceIssueTypeSchema.optional(),
    status: complianceIssueStatusSchema.optional(),
    onlyOpen: z.stringbool().default(false),
});
export const createIssueInputSchema = z.object({
    channelId: z.string().min(1),
    videoRef: z.string().trim().max(120).nullish(),
    videoTitle: z.string().trim().max(200).nullish(),
    type: complianceIssueTypeSchema,
    claimant: z.string().trim().max(120).nullish(),
    dueDate: z.iso.datetime().nullish(),
    status: complianceIssueStatusSchema.default('needs_response'),
    suggestedAction: z.string().trim().max(60).nullish(),
    assigneeId: z.string().nullish(),
    source: complianceSourceSchema.default('manual'),
});
export const updateIssueInputSchema = createIssueInputSchema.partial();
export const issueActionInputSchema = z.object({
    action: z.enum(SUGGESTED_ACTIONS),
    note: z.string().trim().max(2000).nullish(),
});
export const addLogInputSchema = z.object({
    text: z.string().trim().min(1).max(2000),
});
export const checklistTemplateInputSchema = z.object({
    key: z.string().trim().min(1).max(60),
    label: z.string().trim().min(1).max(200),
    order: z.number().int().min(0).max(100).default(0),
    active: z.boolean().default(true),
});
/** 4 thẻ chỉ số của màn hình Bản quyền & tuân thủ. */
export const complianceSummarySchema = z.object({
    copyrightStrikes: z.number().int().nonnegative(),
    communityStrikes: z.number().int().nonnegative(),
    openContentIdClaims: z.number().int().nonnegative(),
    adLimited: z.number().int().nonnegative(),
    /// Sự cố quá hạn phản hồi.
    overdue: z.number().int().nonnegative(),
});
/* ------------------------------------------------------------------ */
/* Bổ sung ở module 09 (chỉ thêm, không sửa phần đã chốt 01b)           */
/* ------------------------------------------------------------------ */
/**
 * Số liệu phụ cho ghi chú dưới 4 thẻ chỉ số của màn hình Bản quyền.
 * `ComplianceSummary` đã chốt ở 01b nên phần này tách thành endpoint riêng
 * `GET /api/v1/compliance/thong-ke`.
 */
export const complianceExtraStatsSchema = z.object({
    /// Số kênh đang theo dõi, để ghi "Toàn bộ N kênh an toàn".
    channelCount: z.number().int().nonnegative(),
    /// Số kênh có ít nhất một gậy bản quyền đang mở.
    channelsWithStrike: z.number().int().nonnegative(),
    /// Claim nhầm từ nhạc nội bộ, đang mở.
    internalMusicClaims: z.number().int().nonnegative(),
    /// Sự cố `ad_limited` đang chờ xem xét lại.
    adLimitedUnderReview: z.number().int().nonnegative(),
});
/** Ánh xạ cột CSV → trường của `ComplianceIssue`. */
export const issueCsvMappingSchema = z.object({
    channelHandle: z.string().trim().min(1).max(100).optional(),
    videoRef: z.string().trim().min(1).max(100).optional(),
    videoTitle: z.string().trim().min(1).max(100).optional(),
    type: z.string().trim().min(1).max(100).optional(),
    claimant: z.string().trim().min(1).max(100).optional(),
    dueDate: z.string().trim().min(1).max(100).optional(),
    status: z.string().trim().min(1).max(100).optional(),
});
/**
 * Nhập sự cố từ CSV xuất tay từ YouTube Studio. Trình duyệt đọc file bằng
 * `FileReader` rồi gửi nội dung; API không nhận multipart.
 */
export const importIssuesInputSchema = z.object({
    csv: z.string().min(1).max(2_000_000),
    mapping: issueCsvMappingSchema.default({}),
});
export const importIssuesResultSchema = z.object({
    created: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    errors: z.array(z.object({ line: z.number().int(), message: z.string() })),
});
/** Kết quả một lượt tự sinh sự cố (job `compliance-tu-sinh` và nút "Quét lại"). */
export const quetLaiResultSchema = z.object({
    daTao: z.number().int().nonnegative(),
    boQua: z.number().int().nonnegative(),
    daCapNhatSucKhoe: z.number().int().nonnegative(),
});
//# sourceMappingURL=compliance.js.map